
import Vue from "vue";
import { mapGetters } from "vuex";
import ChatMessage from "@/components/molecules/ChatMessage.vue";
import ActionButtonGroup, { ActionButtonData } from "@/components/molecules/ActionButtonGroup.vue";
import { logWarn, logError } from "@/lib/utils/logger";
import { ASSIST_MESSAGES } from "@/constants/assistMessages";
import { FeedbackManuscriptMode } from "@/lib/models/assist";
import type { AssistantCatMaster } from "@/lib/models/assistantCat";
import normalModeIcon from "@/assets/img/icon/mode/normal.svg";
import dryModeIcon from "@/assets/img/icon/mode/dry.svg";
import FeedbackManuscriptModeSelectionDialog from "@/components/ui/FeedbackManuscriptModeSelectionDialog.vue";

// 🆕 より具体的で型安全なインターフェース
interface RequestSuggestion {
  id: "feedback" | "matching" | "catchcopy";
  text: string;
  action: "request-feedback" | "request-matching" | "request-catchcopy";
}

interface AssistRequestContentData {
  feedbackSuggestion: RequestSuggestion;
  matchingSuggestion: RequestSuggestion;
  catchcopySuggestion: RequestSuggestion;
  showFeedbackManuscriptModeDialog: boolean;
}

interface AssistRequestContentMethods {
  handleRequestClick(buttonText: string): void;
  executeFeedbackAction(): void;
  executeMatchingAction(): void;
  executeCatchcopyAction(): void;
  handleInfoBannerClose(): void;
  handleIconClick(): void;
  onCloseFeedbackManuscriptModeDialog(): void;
}

interface AssistRequestContentComputed {
  requestButtons: ActionButtonData[];
  shouldShowInfoBanner: boolean;
  assistantCatMaster: AssistantCatMaster | null;
  currentModeIcon: string;
}

export default Vue.extend<AssistRequestContentData, AssistRequestContentMethods, AssistRequestContentComputed>({
  name: "AssistRequestContent",
  components: {
    ChatMessage,
    ActionButtonGroup,
    FeedbackManuscriptModeSelectionDialog,
  },
  data(): AssistRequestContentData {
    return {
      // 感想依頼
      feedbackSuggestion: {
        id: "feedback",
        text: ASSIST_MESSAGES.FEEDBACK_MANUSCRIPT.requestTitle,
        action: "request-feedback",
      },
      // 応募先検索依頼
      matchingSuggestion: {
        id: "matching",
        text: ASSIST_MESSAGES.MATCHING_COMPANY.requestTitle,
        action: "request-matching",
      },
      // キャッチコピー生成依頼
      catchcopySuggestion: {
        id: "catchcopy",
        text: ASSIST_MESSAGES.GENERATE_CATCHCOPY.requestTitle,
        action: "request-catchcopy",
      },
      showFeedbackManuscriptModeDialog: false,
    };
  },
  computed: {
    // 依頼ボタン群の生成
    requestButtons(): ActionButtonData[] {
      return [
        {
          text: this.feedbackSuggestion.text,
          iconSrc: this.currentModeIcon,
          iconClickable: true,
        },
        {
          text: this.matchingSuggestion.text,
        },
        {
          text: this.catchcopySuggestion.text,
        },
      ];
    },

    currentModeIcon(): string {
      const feedbackManuscriptMode = this.assistantCatMaster?.feedbackManuscriptMode ?? FeedbackManuscriptMode.NORMAL;
      return feedbackManuscriptMode === FeedbackManuscriptMode.DRY ? dryModeIcon : normalModeIcon;
    },

    // Vuex gettersをmapしてバナー表示状態を取得
    ...mapGetters("assistNavigationModule", {
      shouldShowInfoBanner: "shouldShowInfoBanner",
    }),
    ...mapGetters("assistantCatModule", {
      assistantCatMaster: "assistantCatMaster",
    }),
  },
  methods: {
    // 依頼ボタン押下処理
    handleRequestClick(buttonText: string): void {
      // 押下されたボタンに応じて親コンポーネントにイベントを送信
      if (buttonText === this.feedbackSuggestion.text) {
        this.executeFeedbackAction();
        this.$emit("suggestion-click", this.feedbackSuggestion);
      } else if (buttonText === this.matchingSuggestion.text) {
        this.executeMatchingAction();
        this.$emit("suggestion-click", this.matchingSuggestion);
      } else if (buttonText === this.catchcopySuggestion.text) {
        this.executeCatchcopyAction();
        this.$emit("suggestion-click", this.catchcopySuggestion);
      } else {
        logWarn({ module: "AssistRequestContent", action: "handleRequestClick" }, "予期しないボタンテキスト", {
          buttonText,
          expected: [this.feedbackSuggestion.text, this.matchingSuggestion.text, this.catchcopySuggestion.text],
        });
      }
    },

    handleIconClick(): void {
      this.showFeedbackManuscriptModeDialog = true;
    },

    // 感想依頼処理
    executeFeedbackAction(): void {
      try {
        // 感想依頼 → 原稿選択画面に遷移
        this.$store.dispatch("assistNavigationModule/showManuscriptSelect");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "executeFeedbackAction" },
          "感想依頼アクション実行エラー",
          error
        );
      }
    },

    // 応募先検索処理
    executeMatchingAction(): void {
      try {
        // 応募先検索 → 原稿選択画面に遷移
        this.$store.dispatch("assistNavigationModule/showManuscriptSelect");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "executeMatchingAction" },
          "応募先検索アクション実行エラー",
          error
        );
      }
    },

    // キャッチコピー生成処理
    executeCatchcopyAction(): void {
      try {
        // キャッチコピー生成 → 原稿選択画面に遷移
        this.$store.dispatch("assistNavigationModule/showManuscriptSelect");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "executeCatchcopyAction" },
          "キャッチコピー生成アクション実行エラー",
          error
        );
      }
    },

    onCloseFeedbackManuscriptModeDialog(): void {
      this.showFeedbackManuscriptModeDialog = false;
    },

    // 🆕 情報バナーのクローズ処理
    handleInfoBannerClose(): void {
      try {
        this.$store.dispatch("assistNavigationModule/dismissInfoBanner");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "handleInfoBannerClose" },
          "情報バナーのクローズエラー",
          error
        );
      }
    },
  },
});
